import { Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import style from "./flow.module.css";
import { useSpring, animated } from "react-spring";
import { useInView } from "react-intersection-observer";

import EkteAILogo from "./../../../../assets/EkteAILogo.png";
import CheckIcon from "./../../../../assets/checkmark.svg";
import WarningIcon from "./../../../../assets/crossRed.svg";
import FUQIcon from "./../../../../assets/thunder.svg";
import HighlightIcon from "./../../../../assets/highlight.svg";

import { IconListSearch, IconTextPlus, IconTextSpellcheck } from "@tabler/icons-react";

const FlowSegment = () => {
    const { t } = useTranslation(['landing-page']);
    
    // Set up intersection observer to detect when element is in view
    const [ref, inView] = useInView({
        threshold: 0.2,
        triggerOnce: true
    });

    // Base delay before animations start
    const baseDelay = 200;
    
    // Animation for the upload box (starting point)
    const uploadAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay,
    });

    // Animation for the scan line
    const scanLineAnimation = useSpring({
        width: inView ? '12.25rem' : '0rem',
        opacity: inView ? 1 : 0,
        from: { width: '0rem', opacity: 0 },
        config: { mass: 1, tension: 280, friction: 40 },
        delay: baseDelay + 200
    });

    // Animation for the original box
    const originalAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 400
    });

    // Animation for the original line
    const originalLineAnimation = useSpring({
        width: inView ? '12.25rem' : '0rem',
        opacity: inView ? 1 : 0,
        from: { width: '0rem', opacity: 0 },
        config: { mass: 1, tension: 280, friction: 40 },
        delay: baseDelay + 500
    });

    // Animation for the fact check box
    const factCheckAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 700
    });

    const languageAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 900
    });

    const textAnalysisAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 1100
    });

    const suggestionsAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 1200
    });

    // Animation for the AI box
    const aiAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 1500 // Slightly after the curve completes
    });

    // Animation for the FUQ line
    const fuqLineAnimation = useSpring({
        width: inView ? '12.25rem' : '0rem',
        opacity: inView ? 1 : 0,
        from: { width: '0rem', opacity: 0 },
        config: { mass: 1, tension: 280, friction: 40 },
        delay: baseDelay + 1700
    });

    // Animation for the FUQ box
    const fuqAnimation = useSpring({
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.8)',
        from: { opacity: 0, transform: 'scale(0.8)' },
        config: { mass: 1, tension: 280, friction: 60 },
        delay: baseDelay + 1900
    });

    const pathAnimation = useSpring({
        strokeDashoffset: inView ? 0 : 1000,
        from: { strokeDashoffset: 1000 },
        config: { mass: 1, tension: 20, friction: 40 },
        delay: baseDelay + 500
    });

    return (
        <div className={style.wrapper}>
            <div className={style.innerWrapper}>
                <Stack spacing={"0.5rem"} mt={"0rem"} m={"auto"}>
                    <h2 className={style.header}>{t('flow.title')}</h2>
                </Stack>

                <div className={style.graphWrapper} ref={ref}>
                    <animated.div className={`${style.box} ${style.upload}`} id={"pdf"} style={uploadAnimation}>
                        <img className={style.boxImg} src={EkteAILogo} alt=""></img>
                        <span className={style.boxText}>{t('flow.check_text')}</span>
                    </animated.div>

                    <animated.div className={`${style.line} ${style.scanLine}`} style={scanLineAnimation} />

                    <animated.div className={`${style.box} ${style.original}`} id={"original"} style={originalAnimation}>
                        <img className={style.boxImg} src={CheckIcon} alt=""></img>
                        <span className={style.boxText}>{t('flow.original')}</span>
                    </animated.div>

                    <animated.div className={`${style.line} ${style.originalLine}`} style={originalLineAnimation} />

                    <animated.div className={`${style.box} ${style.factCheck}`} id={"factCheck"} style={factCheckAnimation}>
                        <IconListSearch color="#2b2b2b" size={"2rem"} className={style.boxImg}/>
                        <span className={style.boxText}>{t('flow.fact_check')}</span>
                    </animated.div>
                    
                    <animated.div className={`${style.box} ${style.language}`} id={"language"} style={languageAnimation}>
                        <IconTextSpellcheck color="#2b2b2b" size={"2rem"} className={style.boxImg}/>
                        <span className={style.boxText}>{t('flow.language_check')}</span>
                    </animated.div>
                    
                    <animated.div className={`${style.box} ${style.textAnalysis}`} id={"textAnalysis"} style={textAnalysisAnimation}>
                        <img className={style.boxImgSmall} src={HighlightIcon} alt=""></img>
                        <span className={style.boxText}>{t('flow.text_analysis')}</span>
                    </animated.div>

                    <animated.div className={`${style.box} ${style.suggestions}`} id={"suggestions"} style={suggestionsAnimation}>
                        <IconTextPlus color="#2b2b2b" size={"2rem"} className={style.boxImg}/>
                        <span className={style.boxText}>{t('flow.language_suggestions')}</span>
                    </animated.div>

                    <div className={style.aiLineCurveContainer}>
                        <svg 
                            width="14.75rem" 
                            height="12.75rem" 
                            viewBox="0 0 235 204" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ position: 'absolute', top: '5.5rem', left: '2.75rem' }}
                        >
                            <animated.path 
                                d="M1 1 v138 q0 64 64 64 h170" 
                                stroke="#AAA" 
                                strokeWidth="1.5" 
                                fill="none"
                                strokeDasharray="1000"
                                style={pathAnimation}
                            />
                        </svg>
                    </div>

                    <animated.div className={`${style.box} ${style.ai}`} id={"ai"} style={aiAnimation}>
                        <img className={style.boxImgSmall} src={WarningIcon} alt=""></img>
                        <span className={style.boxText}>{t('flow.ai_detected')}</span>
                    </animated.div>

                    <animated.div className={`${style.line} ${style.fuqLine}`} style={fuqLineAnimation} />

                    <animated.div className={`${style.box} ${style.fuq}`} id={"fuq"} style={fuqAnimation}>
                        <img className={style.boxImg} src={FUQIcon} alt=""></img>
                        <span className={style.boxText}>{t('flow.follow_up')}</span>
                    </animated.div>
                </div>
            </div>
        </div>
    );
};

export default FlowSegment;
